import { getCascadeConfigForParent } from './ruleEngine';
import { CASCADE_BEHAVIORS } from '../config/rules.config';

export const extractCascadeConfig = (filteredPermissions, rules) => {
    const config = [];

    filteredPermissions.forEach(section => {
        if (!section.parentKey) return;

        const ruleConfig = getCascadeConfigForParent(section.parentKey, rules);
        if (!ruleConfig?.behavior) return;
        const { behavior, checkThreshold } = ruleConfig;

        if (section.type === 'group') {
            const children = [];
            (section.children || []).forEach(child => {
                if (child.type === 'subgroup' && child.children) {
                    children.push({
                        id: child.id,
                        type: 'subgroup',
                        grandchildren: child.children.map(sc => sc.id),
                    });
                } else {
                    children.push({
                        id: child.id,
                        type: 'leaf',
                        grandchildren: (child.templates || []).map(t => t.id),
                    });
                }
            });

            config.push({
                parentKey: section.parentKey,
                behavior,
                checkThreshold,
                sectionType: 'group',
                children,
            });
        } else if (section.type === 'dynamic-group') {
            const children = (section.children || []).map(child => ({
                id: child.id,
                type: 'leaf',
                grandchildren: [],
            }));

            config.push({
                parentKey: section.parentKey,
                behavior,
                checkThreshold,
                sectionType: 'dynamic-group',
                children,
            });
        } else if (section.type === 'dynamic-sectioned') {
            const children = [];
            (section.sections || []).forEach(sub => {
                (sub.permissions || []).forEach(perm => {
                    children.push({
                        id: perm.id,
                        type: 'leaf',
                        grandchildren: (perm.options || []).map(opt => opt.id),
                    });
                });
            });

            config.push({
                parentKey: section.parentKey,
                behavior,
                checkThreshold,
                sectionType: 'dynamic-sectioned',
                children,
            });
        }
    });

    return config;
};

export const getAllPermissionKeysFromTree = (filteredPermissions) => {
    const keys = new Set();

    filteredPermissions.forEach(section => {
        if (section.parentKey) keys.add(section.parentKey);
        if (section.id && section.type === 'single') keys.add(section.id);

        (section.children || []).forEach(child => {
            keys.add(child.id);
            if (child.templates) child.templates.forEach(t => keys.add(t.id));
            if (child.type === 'subgroup' && child.children) {
                child.children.forEach(sc => keys.add(sc.id));
            }
        });

        (section.sections || []).forEach(sub => {
            (sub.permissions || []).forEach(perm => {
                keys.add(perm.id);
                if (perm.options) perm.options.forEach(opt => keys.add(opt.id));
            });
        });
    });

    return keys;
};

export const computeParentSteadyState = (behavior, childIds, values, meta = {}) => {
    if (!childIds || childIds.length === 0) return values;

    switch (behavior) {
        case CASCADE_BEHAVIORS.STRICT_CASCADE:
        case CASCADE_BEHAVIORS.PARTIAL_CASCADE:
        case CASCADE_BEHAVIORS.NOTES_CASCADE:
            return childIds.every(id => !!values[id]);

        case CASCADE_BEHAVIORS.CHILD_SELECTS_PARENT:
        case CASCADE_BEHAVIORS.LETTERS_RULE:
            return childIds.some(id => !!values[id]);

        case CASCADE_BEHAVIORS.MERCURY_INBOX_FINANCIALS_RULES:
            return undefined;

        case CASCADE_BEHAVIORS.THRESHOLD_HYSTERESIS: {
            const checkedCount = childIds.filter(id => !!values[id]).length;
            if (meta.wasOn) {
                const prevCount = meta.prevCheckedCount ?? checkedCount;
                return checkedCount >= prevCount;
            }
            return checkedCount >= (meta.checkThreshold ?? childIds.length);
        }

        default:
            return undefined;
    }
};
